/* eslint-disable react-hooks/exhaustive-deps */
import {
  AdminPanelSettings,
  ContentCopy,
  Edit,
  Link as LinkIcon,
  PointOfSale,
  SettingsEthernet,
} from "@mui/icons-material"
import {
  Chip,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material"
import { useAppDispatch, useAppSelector } from "../../../app/hooks"
import { RootState } from "../../../app/store"
import { useEffect, useState } from "react"
import {
  fetchUsersThunk,
  openEditUserDialogAct,
  openSetUserCodeDialogAct,
} from "../user-list.slice"
import SetUserCodeDialog from "./set-user-code-dialog"
import LoadingIndicator from "../../../app/components/loading-indicator"

const LEVEL_CONFIG: Record<number, { label: string; color: "error" | "info" | "success"; icon: React.ReactNode }> = {
  0: { label: "Admin", color: "error", icon: <AdminPanelSettings fontSize="small" /> },
  1: { label: "Referido", color: "info", icon: <LinkIcon fontSize="small" /> },
  4: { label: "Vendedor", color: "success", icon: <PointOfSale fontSize="small" /> },
}

function getLevelConfig(level?: number) {
  return LEVEL_CONFIG[level ?? 4] ?? LEVEL_CONFIG[4]
}

export default function UsersListTable() {
  const [showSnackBar, setShowSnackBar] = useState(false)
  const [snackMessage, setSnackMessage] = useState("")

  const { users, loading } = useAppSelector((state: RootState) => state.users)
  const dispatch = useAppDispatch()

  useEffect(() => {
    if (users.length === 0) {
      dispatch(fetchUsersThunk())
    }
  }, [users])

  const urlApi = import.meta.env.VITE_LANDING_PAGE

  const copyReferralLink = (code: string) => {
    navigator.clipboard.writeText(`${urlApi}?code=${code}`)
    setSnackMessage("Enlace de referido copiado")
    setShowSnackBar(true)
  }

  return (
    <>
      <Snackbar
        open={showSnackBar}
        autoHideDuration={4000}
        onClose={() => setShowSnackBar(false)}
        message={snackMessage}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
      <LoadingIndicator open={loading} />
      <SetUserCodeDialog />

      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "action.hover" }}>
                <TableCell>Usuario</TableCell>
                <TableCell>Rol</TableCell>
                <TableCell>Contacto</TableCell>
                <TableCell>Código referido</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                    <Typography variant="body2" color="text.secondary">
                      No hay usuarios registrados. Crea el primero con el botón &quot;Nuevo usuario&quot;.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {users.map((el) => {
                const level = getLevelConfig(el.level)
                return (
                  <TableRow
                    key={el._id}
                    hover
                    sx={{
                      transition: "background-color 0.2s",
                      "&:last-child td": { border: 0 },
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>
                        {el.name} {el.lastName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {el.email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        icon={level.icon as React.ReactElement}
                        label={level.label}
                        color={level.color}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{el.phone}</Typography>
                    </TableCell>
                    <TableCell>
                      {el.code ? (
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <Tooltip title={`${urlApi}?code=${el.code}`}>
                            <Chip
                              size="small"
                              label={el.code}
                              variant="outlined"
                              onClick={() => copyReferralLink(el.code!)}
                              sx={{ cursor: "pointer" }}
                            />
                          </Tooltip>
                          <Tooltip title="Copiar enlace">
                            <IconButton
                              size="small"
                              onClick={() => copyReferralLink(el.code!)}
                              sx={{ cursor: "pointer" }}
                            >
                              <ContentCopy fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      ) : (
                        <Tooltip title="Asignar código de referido">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() =>
                              dispatch(
                                openSetUserCodeDialogAct({
                                  userId: el._id!,
                                  userName: el.name,
                                }),
                              )
                            }
                            sx={{ cursor: "pointer" }}
                          >
                            <SettingsEthernet fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Editar usuario">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => dispatch(openEditUserDialogAct(el._id!))}
                          sx={{ cursor: "pointer" }}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </>
  )
}
